/**
 * Unified report period filter: rolling days + natural calendar months.
 * Always emits inclusive startDate/endDate via ReportPeriod.
 */

import { Calendar, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '@/i18n';
import {
  canShiftMonthForward,
  createDefaultPeriod,
  defaultMonthOptions,
  matchDayPreset,
  resolveDayPreset,
  resolveMonthPreset,
  resolveMonthSingle,
  shiftMonth,
  type DayPresetId,
  type MonthPresetId,
  type PeriodMode,
  type ReportPeriod,
} from '@/features/reports/dateRange';

export interface DateRangeSelectorProps {
  period: ReportPeriod;
  onPeriodChange: (period: ReportPeriod) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  className?: string;
  /** Hide day mode toggle/content (default false). */
  hideDayMode?: boolean;
  /** Hide month mode toggle/content (default false). */
  hideMonthMode?: boolean;
  /** Consumer-supported preset ids; defaults to all shared presets. */
  dayPresetIds?: Exclude<DayPresetId, 'custom'>[];
  monthPresetIds?: Exclude<MonthPresetId, 'custom'>[];
} 

const DAY_PRESETS: { id: Exclude<DayPresetId, 'custom'>; labelKey: string }[] = [
  { id: 'last_7d', labelKey: 'reportsPage.dateRangeSelector.last7Days' },
  { id: 'last_30d', labelKey: 'reportsPage.dateRangeSelector.last30Days' },
  { id: 'last_90d', labelKey: 'reportsPage.dateRangeSelector.last90Days' },
  { id: 'last_365d', labelKey: 'reportsPage.dateRangeSelector.last1Year' },
];

const MONTH_PRESETS: { id: Exclude<MonthPresetId, 'custom'>; labelKey: string }[] = [
  { id: 'this_month', labelKey: 'reportsPage.dateRangeSelector.thisMonth' },
  { id: 'last_month', labelKey: 'reportsPage.dateRangeSelector.lastMonth' },
  { id: 'last_3_months', labelKey: 'reportsPage.dateRangeSelector.last3Months' },
  { id: 'last_6_months', labelKey: 'reportsPage.dateRangeSelector.last6Months' },
  { id: 'last_12_months', labelKey: 'reportsPage.dateRangeSelector.last12Months' },
];

function chipClass(active: boolean): string {
  return clsx(
    'px-3 py-1.5 text-sm rounded-lg transition-colors',
    active
      ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
      : 'bg-gray-100 text-gray-900 hover:bg-gray-200',
  );
}

function formatMonthLabel(monthKey: string, language: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: 'long',
  });
}

export function DateRangeSelector({
  period,
  onPeriodChange,
  onRefresh,
  isRefreshing,
  className,
  hideDayMode = false,
  hideMonthMode = false,
  dayPresetIds,
  monthPresetIds,
}: DateRangeSelectorProps) {
  const { t, language } = useI18n();
  const mode: PeriodMode = period.mode;
  const monthOptions = defaultMonthOptions();
  const visibleDayPresets = dayPresetIds
    ? DAY_PRESETS.filter((preset) => dayPresetIds.includes(preset.id))
    : DAY_PRESETS;
  const visibleMonthPresets = monthPresetIds
    ? MONTH_PRESETS.filter((preset) => monthPresetIds.includes(preset.id))
    : MONTH_PRESETS;

  const setMode = (next: PeriodMode) => {
    if (next === mode) return;
    if (next === 'day') {
      onPeriodChange(createDefaultPeriod({ mode: 'day', dayPreset: 'last_30d' }));
    } else {
      onPeriodChange(createDefaultPeriod({ mode: 'month', monthPreset: 'this_month' }));
    }
  };

  const selectDayPreset = (presetId: Exclude<DayPresetId, 'custom'>) => {
    onPeriodChange(resolveDayPreset(presetId));
  };

  const selectMonthPreset = (presetId: Exclude<MonthPresetId, 'custom'>) => {
    onPeriodChange(resolveMonthPreset(presetId));
  };

  const onDayStartChange = (startDate: string) => {
    const endDate = period.endDate;
    onPeriodChange({
      mode: 'day',
      presetId: matchDayPreset(startDate, endDate),
      startDate,
      endDate,
    });
  };

  const onDayEndChange = (endDate: string) => {
    const startDate = period.startDate;
    onPeriodChange({
      mode: 'day',
      presetId: matchDayPreset(startDate, endDate),
      startDate,
      endDate,
    });
  };

  const singleMonth = period.mode === 'month' && period.kind === 'single' ? period.month : null;
  const showMonthNav = singleMonth !== null;

  const goPrevMonth = () => {
    if (!singleMonth) return;
    onPeriodChange(resolveMonthSingle(shiftMonth(singleMonth, -1)));
  };

  const goNextMonth = () => {
    if (!singleMonth || !canShiftMonthForward(singleMonth)) return;
    onPeriodChange(resolveMonthSingle(shiftMonth(singleMonth, 1)));
  };

  const onMonthSelect = (monthKey: string) => {
    onPeriodChange(resolveMonthSingle(monthKey));
  };

  const activeDayPreset = period.mode === 'day' ? period.presetId : null;
  const activeMonthPreset = period.mode === 'month' ? period.presetId : null;

  const showModeToggle = !hideDayMode && !hideMonthMode;

  const summaryParts = [period.startDate, period.endDate];
  let summaryExtra = '';
  if (period.mode === 'month') {
    if (period.kind === 'single') {
      const isInProgress = period.endDate !== monthLastDayString(period.month);
      summaryExtra = isInProgress
        ? t('reportsPage.dateRangeSelector.inProgressMonth')
        : t('reportsPage.dateRangeSelector.naturalMonth');
    } else {
      summaryExtra = t('reportsPage.dateRangeSelector.naturalMonthRange');
    }
  }

  return (
    <div className={clsx('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
        {showModeToggle && (
          <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1 shrink-0">
            <button
              type="button"
              onClick={() => setMode('day')}
              aria-pressed={mode === 'day'}
              className={clsx(
                'px-3 py-1.5 text-sm rounded-md transition-colors',
                mode === 'day'
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {t('reportsPage.dateRangeSelector.modeDay')}
            </button>
            <button
              type="button"
              onClick={() => setMode('month')}
              aria-pressed={mode === 'month'}
              className={clsx(
                'px-3 py-1.5 text-sm rounded-md transition-colors',
                mode === 'month'
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {t('reportsPage.dateRangeSelector.modeMonth')}
            </button>
          </div>
        )}

        {mode === 'day' && !hideDayMode && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {visibleDayPresets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => selectDayPreset(preset.id)}
                  aria-pressed={activeDayPreset === preset.id}
                  className={chipClass(activeDayPreset === preset.id)}
                >
                  {t(preset.labelKey)}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={period.startDate}
                  max={period.endDate || undefined}
                  onChange={(e) => onDayStartChange(e.target.value)}
                  className="w-[10.5rem] pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <span className="text-sm text-gray-500">{t('reportsPage.dateRangeSelector.to')}</span>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={period.endDate}
                  min={period.startDate || undefined}
                  onChange={(e) => onDayEndChange(e.target.value)}
                  className="w-[10.5rem] pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {onRefresh && <RefreshButton onRefresh={onRefresh} isRefreshing={isRefreshing} />}
            </div>
          </>
        )}

        {mode === 'month' && !hideMonthMode && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {visibleMonthPresets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => selectMonthPreset(preset.id)}
                  aria-pressed={activeMonthPreset === preset.id}
                  className={chipClass(activeMonthPreset === preset.id)}
                >
                  {t(preset.labelKey)}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
              {showMonthNav && singleMonth && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={goPrevMonth}
                    aria-label={t('reportsPage.dateRangeSelector.prevMonth')}
                    className="p-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <select
                    value={singleMonth}
                    onChange={(e) => onMonthSelect(e.target.value)}
                    aria-label={t('reportsPage.dateRangeSelector.selectMonth')}
                    className="min-w-[9.5rem] px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {!monthOptions.includes(singleMonth) && (
                      <option value={singleMonth}>{formatMonthLabel(singleMonth, language)}</option>
                    )}
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>
                        {formatMonthLabel(m, language)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={goNextMonth}
                    disabled={!canShiftMonthForward(singleMonth)}
                    aria-label={t('reportsPage.dateRangeSelector.nextMonth')}
                    className={clsx(
                      'p-1.5 rounded-lg transition-colors',
                      canShiftMonthForward(singleMonth)
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-gray-50 text-gray-300 cursor-not-allowed',
                    )}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              {onRefresh && <RefreshButton onRefresh={onRefresh} isRefreshing={isRefreshing} />}
            </div>
          </>
        )}
      </div>

      <p className="text-xs text-gray-500">
        {t('reportsPage.dateRangeSelector.rangeSummary', {
          start: summaryParts[0],
          end: summaryParts[1],
        })}
        {summaryExtra ? ` · ${summaryExtra}` : ''}
      </p>
    </div>
  );
}

function RefreshButton({
  onRefresh,
  isRefreshing,
}: {
  onRefresh: () => void;
  isRefreshing?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={isRefreshing}
      className={clsx(
        'p-2 rounded-lg transition-colors',
        isRefreshing
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-blue-100 text-blue-600 hover:bg-blue-200',
      )}
    >
      <RefreshCw className={clsx('w-4 h-4', isRefreshing && 'animate-spin')} />
    </button>
  );
}

/** Last calendar day YYYY-MM-DD for a YYYY-MM key (local). */
function monthLastDayString(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  const last = new Date(y, m, 0);
  const year = last.getFullYear();
  const month = String(last.getMonth() + 1).padStart(2, '0');
  const day = String(last.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** @deprecated Prefer period-based API; kept for any residual call sites. */
export type { ReportPeriod };
